import { SpaCareReturn } from '../components/SpaCareReturn';
import { useEffect, useId, useState } from 'react';
import { ArrowRightIcon, ChevronDownIcon, MessageSquareIcon, PhoneIcon } from 'lucide-react';
import { business, deliveryBargeAlt, deliveryDockAlt, images, routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';
import { Accordion } from '../components/Accordion';
import { serviceSymptoms as symptoms } from '../data/serviceTroubleshooting';
const TITLE = 'Hot Tub Service & Repair in Brooklyn, Michigan | Pegasus Spas';
const DESCRIPTION = 'Tell us what your spa is doing and get practical help from our own technicians. We service all makes and models from our Brooklyn, Michigan showroom.';
const CANONICAL = 'https://www.pegasus-spas.com/SpaServiceandRepair.html';

/** Real Pegasus lake-access delivery photographs supplied by the owner. */
const DOCK_PHOTO = images.deliveryDock;
const BARGE_PHOTO = images.deliveryBarge;

/** What our service department actually does, grouped for quick scanning. */
const capabilityGroups = [{
  title: 'Repairs & Parts',
  accent: 'border-pegasus-magenta',
  items: ['Repairs on all makes and models, whether you bought from us or not', 'Tracking down the right replacement parts', 'Retrofit parts when the original is no longer made', 'Reconditioning vintage and freeze-damaged spas']
}, {
  title: 'Warranty & Seasonal',
  accent: 'border-pine',
  items: ['Warranty service for the spas we sell, performed by our own factory-certified technicians', 'Winterizing and bagging']
}, {
  title: 'Moves & Special Jobs',
  accent: 'border-pegasus-blue',
  items: ['Relocations on the same property or to a new home', 'Difficult access: fences, decks, lifts, cranes and lake crossings', 'Damage evaluations for insurance claims']
}];
const steps = [{
  title: 'Call or text us.',
  body: 'Describe what you are seeing. A photo or short video helps, but it is optional.'
}, {
  title: 'We may sort it out by phone.',
  body: 'Sometimes a question or two does it. If not, we will tell you exactly what would be useful.'
}, {
  title: 'One of our own technicians comes out.',
  body: 'We do not subcontract service. The person at your spa works for us.'
}];
const practicalDetails = [{
  term: 'Rotating or moving a spa on the same property',
  detail: 'Call first. Whether it can be done depends on the access and on the existing electrical connection and wire.'
}, {
  term: 'More involved moves',
  detail: 'Some moves call for electrical changes, a lift, a crane or other planning before anything is lifted.'
}, {
  term: 'Moving a spa to a new home or a new spot in the backyard',
  detail: 'We can take this on, including spas that were purchased somewhere else. Send photos of both ends of the route.'
}, {
  term: 'Winterization and bagging',
  detail: 'We can winterize and bag a spa that will be out of use for the season. Call us to discuss your plans.'
}, {
  term: 'If the same problem comes back within 30 days',
  detail: 'If the exact same problem returns within 30 days and a related part is needed, there is no additional service-call fee. You pay for the part and the labor, and parts carry the applicable manufacturer warranty.'
}, {
  term: 'Warranty questions',
  detail: 'We handle warranty service for the spas we sell with our own factory-certified technicians, without subcontracting to third-party service companies. Call us first so we can check the applicable manufacturer coverage and arrange the next step.'
}, {
  term: 'After hours',
  detail: 'We are not an on-call emergency service, but you are welcome to reach out and we will help when we are able.'
}];
const serviceArea = ['Ann Arbor', 'Adrian', 'Albion', 'Blissfield', 'Bridgewater', 'Brooklyn', 'Camden', 'Cement City', 'Chelsea', 'Clark Lake', 'Dansville', 'East Lansing', 'Grass Lake', 'Gregory', 'Hamburg', 'Hanover', 'Hillsdale', 'Holt', 'Homer', 'Horton', 'Howell', 'Hudson', 'Jackson', 'Jerome', 'Jonesville', 'Lansing', 'Lambertville', 'Leslie', 'Litchfield', 'Manchester', 'Manitou Beach', 'Marshall', 'Michigan Center', 'Montgomery', 'Morenci', 'Moscow', 'Mosherville', 'Napoleon', 'North Adams', 'Norvell', 'Onsted', 'Osseo', 'Ottawa Lake', 'Palmyra', 'Petersburg', 'Pinckney', 'Pittsford', 'Pleasant Lake', 'Quincy', 'Reading', 'Ridgeway', 'Rives Junction', 'Saline', 'Sand Creek', 'Somerset', 'Somerset Center', 'Spring Arbor', 'Stockbridge', 'Tecumseh', 'Temperance', 'Tipton', 'Waldron', 'Whitmore Lake', 'Williamston'];
const relatedLinks = [{
  label: 'Spa Care',
  href: routes.spaCare
}, {
  label: 'Service Work Guarantee',
  href: routes.serviceGuarantee
}, {
  label: 'Shop Filters',
  href: routes.filters
}, {
  label: 'Supplies & Chemicals',
  href: routes.supplies
}];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Plain-language symptom chooser. One row open at a time, keyboard operable. */
function SymptomChooser() {
  const [openIndex, setOpenIndex] = useState(-1);
  const baseId = useId();
  return <ul className="mt-8 max-w-3xl border-t border-ink/15" data-id="element-1850" data-mp="1xqmez3ahayyn">
      {symptoms.map((symptom, index) => {
      const isOpen = openIndex === index;
      const panelId = `${baseId}-symptom-${index}`;
      const buttonId = `${baseId}-symptom-button-${index}`;
      return <li key={symptom.label} className="border-b border-ink/15" data-id="element-1851" data-mp="1a0s6exx6zwk7" data-mpi={index}>
            <button type="button" id={buttonId} aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpenIndex(isOpen ? -1 : index)} className="flex w-full min-h-[60px] items-center justify-between gap-4 py-4 text-left transition-colors duration-150 ease-out hover:text-pegasus-blue" data-id="element-1852" data-mp="19yyylfauf6am">
              <span className="text-[19px] font-semibold leading-7 text-ink sm:text-[21px]" data-id="element-1853" data-mp="2i2xpjoootbr0">
                {symptom.label}
              </span>
              <ChevronDownIcon className={`h-5 w-5 shrink-0 text-pegasus-magenta transition-transform duration-150 ease-out motion-reduce:transition-none ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1854" data-mp="1pe0bjxrie142" />
            </button>
            <div id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen} className="pb-5 pr-2" data-id="element-1855" data-mp="3es5dt4goxyym">
              <ul className="list-disc space-y-3 border-l-2 border-pegasus-magenta pl-9 text-[16.5px] leading-7 text-ink/80" data-id="element-1856" data-mp="3ia2wspyi18ef">
                {symptom.help.map(tip => <li key={tip} data-id="element-1857" data-mp="3nb0dsz7rvwec">{tip}</li>)}
              </ul>
            </div>
          </li>;
    })}
    </ul>;
}
export function ServicePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-1858" data-mp="3eoysc2e752">
      <SpaCareReturn data-id="element-1859" data-mp="2wo1iegj4qt" />
      {/* Symptom-first opening */}
      <section aria-labelledby="service-h1" className="w-full bg-white" data-id="element-1860" data-mp="ntiux6cq6iz8">
        <div className="mx-auto max-w-container px-5 pb-10 pt-10 sm:px-8 lg:pb-12 lg:pt-14" data-id="element-1861" data-mp="sl0nb60onb8l">
          <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-12 lg:gap-10" data-id="element-1862" data-mp="2oo6piap8sh9y">
            <div className="lg:col-span-8" data-id="element-1863" data-mp="2jxqbaodlkrmj">
              <p className="text-[14px] font-bold uppercase tracking-[0.02em] text-pegasus-blue" data-id="element-1864" data-mp="2jwnj8d4jgj51">
                Spa Service &amp; Repair
              </p>
              <h1 id="service-h1" className="mt-3 max-w-3xl font-display text-h1-mobile font-semibold text-ink lg:text-[52px] lg:leading-[60px]" data-id="element-1865" data-mp="2ax0lq2exjxh9">
                Tell Us What Your Spa Is Doing
              </h1>
              <p className="mt-5 max-w-2xl text-body text-ink/80 sm:text-body-lg" data-id="element-1866" data-mp="33m7fyp7cq0wp">
                You do not need a part number or a technical diagnosis before you call. Tell us what
                you see, hear or feel, and we will help narrow down the next step.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-1867" data-mp="1n88vhgkwhp74">
                <SiteLink to={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1868" data-mp="1i7tl8xvx0xtd">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1869" data-mp="1i2vnsgip6ufi" />
                  Call or Text {business.phone}
                </SiteLink>
                <SiteLink to={routes.contact} className="inline-flex min-h-[48px] items-center rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-medium text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-1870" data-mp="1muvjtxvnd36t">
                  Request Service
                </SiteLink>
              </div>
            </div>

            <div className="lg:col-span-4" data-id="element-1871" data-mp="25qvgvn3hewx9">
              <div className="aspect-[3/4] w-full max-w-[320px] overflow-hidden rounded-card lg:ml-auto lg:max-w-none" data-id="element-1872" data-mp="6f2efaxxczd5">
                <img src={images.pathService} alt="Technician checking the plumbing inside an open hot tub equipment bay" className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-1873" data-mp="bdnftdowxp36" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Symptom chooser */}
      <section aria-labelledby="symptom-heading" className="w-full bg-water" data-id="element-1874" data-mp="2x5ek8isx7wkf">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1875" data-mp="324hq25gq66tg">
          <h2 id="symptom-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1876" data-mp="372j94qe5i64x">
            What&rsquo;s the Spa Doing?
          </h2>
          <p className="mt-4 max-w-2xl text-body text-ink/80 sm:text-body-lg" data-id="element-1877" data-mp="3fvmiamhrjwtr">
            Start with these basic checks. Follow the owner&rsquo;s manual for your particular spa,
            and call us if you are unsure.
          </p>

          <SymptomChooser data-id="element-1878" data-mp="3pu27gjx8cw0m" />

          <p className="mt-6 max-w-3xl text-[16.5px] leading-7 text-ink/75" data-id="element-1879" data-mp="3pr694uhh05bn">
            Lost heat or circulation in freezing weather needs prompt attention. Do not drain
            the spa or switch it off as a general troubleshooting step; follow any shutdown
            instruction in your owner&rsquo;s manual and arrange help to protect the plumbing.
            Do not wait for an online reply when the spa may freeze.
          </p>
        </div>
      </section>

      {/* What we work on */}
      <section aria-labelledby="capabilities-heading" className="w-full bg-white" data-id="element-1880" data-mp="mkff4xtv4j6i">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-1881" data-mp="hjzzfzm3yzld">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-end lg:gap-10" data-id="element-1882" data-mp="cr6upbqn3r9a">
            <h2 id="capabilities-heading" className="font-display text-h2-mobile font-semibold text-ink lg:col-span-6 lg:text-h2-desktop" data-id="element-1883" data-mp="8pv5h7boh81m">
              Services We Offer
            </h2>
            <p className="text-body text-ink/80 sm:text-body-lg lg:col-span-6" data-id="element-1884" data-mp="10gsv22ir0civ">
              From a heater that quit to a spa that needs to cross a lake, this is the everyday work
              of our service department.
            </p>
          </div>

          <div className="mt-9 grid grid-cols-1 gap-x-10 gap-y-8 md:grid-cols-3" data-id="element-1885" data-mp="2l0wkvaeuwmsk">
            {capabilityGroups.map(group => <div key={group.title} className={`border-t-2 ${group.accent} pt-5`} data-id="element-1886" data-mp="2pxn1pzyhjmf1">
                <h3 className="font-display text-[22px] font-semibold leading-8 text-ink" data-id="element-1887" data-mp="2bynbinvbqzgr">
                  {group.title}
                </h3>
                <ul className="mt-3 divide-y divide-ink/10" data-id="element-1888" data-mp="2gw6qjpzujg9r">
                  {group.items.map(item => <li key={item} className="py-2.5 text-[16.5px] leading-6 text-ink/80" data-id="element-1889" data-mp="270dhiglc64wq">
                      {item}
                    </li>)}
                </ul>
              </div>)}
          </div>

          <p className="mt-8 text-[16.5px] leading-7 text-ink/70" data-id="element-1890" data-mp="57xlpjm1pukr">
            Warranty service for the spas we sell is handled by our own factory-certified technicians. We do not subcontract that work to third-party service companies.
          </p>
        </div>
      </section>

      {/* How it works, plainly */}
      <section aria-labelledby="after-heading" className="w-full border-y border-ink/10 bg-water" data-id="element-1891" data-mp="1zhm7vtypb2kc">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-1892" data-mp="1upcfm7a45qew">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14" data-id="element-1893" data-mp="28gr60z6cotvz">
            <div className="lg:col-span-5" data-id="element-1894" data-mp="24g78vvls1q9u">
              <h2 id="after-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1895" data-mp="24fe0kq766spl">
                How Service Works With Us
              </h2>
              <p className="mt-6 border-l-2 border-pegasus-magenta pl-5 font-display text-[23px] font-semibold leading-9 text-ink sm:text-[26px]" data-id="element-1896" data-mp="2desslu2zjbc7">
                If a repair is not worth it, we will tell you honestly rather than keep spending
                your money.
              </p>
            </div>

            <ol className="lg:col-span-7" data-id="element-1897" data-mp="piunurwwkys2">
              {steps.map((step, index) => <li key={step.title} className="flex gap-5 border-b border-ink/15 py-5 first:pt-0 last:border-b-0" data-id="element-1898" data-mp="pknxmg2tbz4a" data-mpi={index}>
                  <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink font-display text-[16px] font-semibold text-white" data-id="element-1899" data-mp="zgik5ma5qecc">
                    {index + 1}
                  </span>
                  <div data-id="element-1900" data-mp="bqoafdl4x5wb">
                    <h3 className="text-[19px] font-semibold leading-7 text-ink" data-id="element-1901" data-mp="bpd5hafyds3m">{step.title}</h3>
                    <p className="mt-1.5 text-[16.5px] leading-7 text-ink/75" data-id="element-1902" data-mp="gk97n3ewlvug">{step.body}</p>
                  </div>
                </li>)}
            </ol>
          </div>
        </div>
      </section>

      {/* Real photo proof */}
      <section aria-labelledby="access-heading" className="w-full bg-ink" data-id="element-1903" data-mp="21cn6iljqhnr2">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-1904" data-mp="1xajspq9le72m">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12" data-id="element-1905" data-mp="1x8feofhzbrom">
            <div className="lg:col-span-4 lg:pt-4" data-id="element-1906" data-mp="1s9vrkbrng9uo">
              <h2 id="access-heading" className="font-display text-h2-mobile font-semibold text-white lg:text-h2-desktop" data-id="element-1907" data-mp="1ncm4h2rqgaa0">
                Got a Hot Tub in a Tricky Spot?
              </h2>
              <p className="mt-4 text-body text-white/85 sm:text-body-lg" data-id="element-1908" data-mp="1dlr8v7sljybw">
                Fences, decks, tight side yards, lifts and cranes can change the plan. In
                lake-access settings, boats may also be part of the logistics &mdash; and when a
                cottage can only be reached from the water, we have loaded a spa onto a barge and
                taken it across the lake. We can look at the access, explain what may be involved
                and help coordinate a realistic way to move or service the spa.
              </p>
              <SiteLink to={business.phoneHref} className="mt-7 inline-flex min-h-[48px] items-center gap-2 rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1909" data-mp="pyw1bzlf1ddq">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1910" data-mp="g2sx8o7074o5" />
                Talk Through Your Access
              </SiteLink>
            </div>

            <figure className="lg:col-span-8" data-id="element-1911" data-mp="18oc2mp4xd9v7">
              <div className="grid grid-cols-12 gap-4 sm:gap-5" data-id="element-1912" data-mp="uy8m8esq8ado">
                <div className="relative col-span-12 aspect-[4/3] overflow-hidden rounded-card sm:col-span-8" data-id="element-1913" data-mp="uxqrjmlpq47v">
                  <img src={DOCK_PHOTO} alt={deliveryDockAlt} className="absolute inset-0 h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-1914" data-mp="yxr6r9gmyjfz" />
                </div>
                <div className="relative col-span-12 aspect-[4/3] overflow-hidden rounded-card sm:col-span-4 sm:aspect-auto" data-id="element-1915" data-mp="3c4h1fem209o9">
                  <img src={BARGE_PHOTO} alt={deliveryBargeAlt} className="absolute inset-0 h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-1916" data-mp="3lw3kfx4mvd1k" />
                </div>
              </div>
              <figcaption className="mt-4 text-[15.5px] leading-6 text-white/70" data-id="element-1917" data-mp="2o9o4x0vropgn">
                One of our own lake-access deliveries. Every property is different, so we work out
                the approach job by job.
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* Practical details */}
      <section aria-labelledby="details-heading" className="w-full bg-white" data-id="element-1918" data-mp="ihr3nah1vhzi">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1919" data-mp="dg1phlyj6o77">
          <h2 id="details-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1920" data-mp="270brmcelpseh">
            Good to Know
          </h2>

          <dl className="mt-8 max-w-4xl border-t border-ink/15" data-id="element-1921" data-mp="2gwf4ac72x9fo">
            {practicalDetails.map(item => <div key={item.term} className="grid grid-cols-1 gap-x-8 gap-y-1 border-b border-ink/15 py-5 lg:grid-cols-12" data-id="element-1922" data-mp="2loyhv95ro4kh">
                <dt className="text-[17.5px] font-semibold leading-7 text-ink lg:col-span-5" data-id="element-1923" data-mp="2uo3gefhbvxwn">
                  {item.term}
                </dt>
                <dd className="text-[16.5px] leading-7 text-ink/75 lg:col-span-7" data-id="element-1924" data-mp="2zor2cmqp9ok0">
                  {item.detail}
                </dd>
              </div>)}
          </dl>

          <SiteLink to={routes.serviceGuarantee} className="mt-7 inline-flex min-h-[48px] items-center gap-1.5 text-[16.5px] font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1925" data-mp="15bhqwpelfsz4">
            Read the Service Work Guarantee
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1926" data-mp="t0h31k07hf0a" />
          </SiteLink>
        </div>
      </section>

      {/* Service area */}
      <section aria-labelledby="area-heading" className="w-full border-t border-ink/10 bg-water" data-id="element-1927" data-mp="1locvgciqskr9">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1928" data-mp="1gpa1bn4avyy2">
          <h2 id="area-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1929" data-mp="1gn716a9fopfm">
            Service Area
          </h2>
          <p className="mt-4 max-w-3xl text-body text-ink/80 sm:text-body-lg" data-id="element-1930" data-mp="16qu8nfpmkhmg">
            We serve Brooklyn and the surrounding Michigan communities. Availability varies by
            location, equipment and scheduling, so get in touch and we will let you know what we can
            do.
          </p>

          <div className="mt-7 max-w-4xl" data-id="element-1931" data-mp="2rb5d5umf4f8e">
            <Accordion defaultOpenIndex={-1} items={[{
            question: 'Communities we serve',
            answer: <ul className="grid grid-cols-1 border-t border-ink/15 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1933" data-mp="2cl7shonp67ny">
                      {serviceArea.map(city => <li key={city} className="border-b border-ink/10 py-2 pr-4 text-[16px] leading-6 text-ink/85" data-id="element-1934" data-mp="3a7x1iigthkjb">
                          {city}
                        </li>)}
                    </ul>
          }]} data-id="element-1932" data-mp="2mily0t831c1h" />
          </div>
        </div>
      </section>

      {/* Warm close */}
      <section aria-labelledby="close-heading" className="w-full border-t border-ink/10 bg-white" data-id="element-1935" data-mp="1ku0akz840jxu">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1936" data-mp="bzkx46vbj6l">
          <div className="max-w-2xl border-l-2 border-pegasus-magenta pl-6 sm:pl-8" data-id="element-1937" data-mp="9wkrrc049o5">
            <h2 id="close-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-[34px] lg:leading-[42px]" data-id="element-1938" data-mp="4b8by1aix2yg">
              Not Sure What to Call It? That&rsquo;s Okay.
            </h2>
            <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-1939" data-mp="j30flg0ygskc">
              Call or text us and describe what is happening. Send a photo if it is handy. We will
              help you figure out what matters and what the next step should be.
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-1940" data-mp="x204mosdr9h1">
              <SiteLink to={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1941" data-mp="x176udbefub1">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1942" data-mp="5zo3anacp9qb" />
                Call or Text {business.phone}
              </SiteLink>
              <SiteLink to={routes.contact} className="inline-flex min-h-[48px] items-center gap-2 rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-medium text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-1943" data-mp="124ptplh90zf">
                <MessageSquareIcon className="h-4 w-4" aria-hidden="true" data-id="element-1944" data-mp="ovmo0j1385nb" />
                Send a Message
              </SiteLink>
            </div>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section aria-label="Related pages" className="w-full border-t border-ink/10 bg-white" data-id="element-1945" data-mp="toft580gxfzx">
        <div className="mx-auto max-w-container px-5 py-6 sm:px-8" data-id="element-1946" data-mp="17o8dmlleizzg">
          <ul className="flex flex-col gap-x-8 gap-y-1 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-1947" data-mp="17oi2u6ymh343">
            {relatedLinks.map(link => <li key={link.label} data-id="element-1948" data-mp="1mb74aecfpqd4">
                <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1949" data-mp="1mc8op9fcbp7i">
                  {link.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1950" data-mp="13jel1bn63jil" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>
    </main>;
}